/**
 * Module: `queries/conversation-questions.ts` — durable registry + TanStack DB sync.
 * @remarks Questions bootstrap from GET /conversations/db; PATCH reconciles after hydrate.
 */

import { apiClient } from "@/lib/api";
import { deriveConversationQuestionsFromMessages } from "@/lib/qualification-lifecycle/normalize-transcript";
import {
  conversationContentCollection,
  conversationQuestionsCollection,
} from "@/tanstack-db/collections";
import type { ConversationQuestionsItem } from "@/tanstack-db/schemas";
import type { ConversationQuestion } from "@/types";

const pendingUpserts = new Map<string, ConversationQuestionsItem>();
let flushRetryTimer: ReturnType<typeof setTimeout> | null = null;

/** Last fingerprint sent per conversation — skip redundant PATCH. */
const lastPersistedFingerprint = new Map<string, string>();

function questionsFingerprint(questions: ConversationQuestion[]): string {
  return JSON.stringify(
    questions.map((q) => ({
      id: q.id,
      content: q.content,
      timestamp: q.timestamp,
    }))
  );
}

function queuePendingUpsert(items: ConversationQuestionsItem[]): void {
  for (const item of items) {
    pendingUpserts.set(item.conversationId, item);
  }
}

function flushPendingUpserts(): void {
  if (pendingUpserts.size === 0) return;
  const rows = [...pendingUpserts.values()];
  pendingUpserts.clear();
  conversationQuestionsCollection.utils.writeUpsert(rows);
}

function scheduleFlushPendingUpserts(): void {
  if (flushRetryTimer) return;
  flushRetryTimer = setTimeout(() => {
    flushRetryTimer = null;
    if (!conversationQuestionsCollection.isReady()) {
      scheduleFlushPendingUpserts();
      return;
    }
    flushPendingUpserts();
  }, 50);
}

function safeWriteUpsert(items: ConversationQuestionsItem[]): void {
  if (items.length === 0) return;
  if (conversationQuestionsCollection.isReady()) {
    conversationQuestionsCollection.utils.writeUpsert(items);
    return;
  }
  queuePendingUpsert(items);
  scheduleFlushPendingUpserts();
}

/** Bootstrap conversationQuestionsCollection from GET /conversations/db payload. */
export function bootstrapConversationQuestionsFromDbList(
  dbConversations: {
    dustConversationId: string;
    questions?: ConversationQuestion[];
  }[]
): void {
  const items: ConversationQuestionsItem[] = dbConversations
    .filter((row) => row.dustConversationId)
    .map((row) => ({
      conversationId: row.dustConversationId,
      questions: row.questions ?? [],
    }));
  if (items.length === 0) return;
  safeWriteUpsert(items);
  for (const item of items) {
    lastPersistedFingerprint.set(
      item.conversationId,
      questionsFingerprint(item.questions)
    );
  }
}

/** Apply server-persisted registry rows (backfill) — no client PATCH. */
export function applyServerConversationQuestionsRegistry(
  items: ConversationQuestionsItem[]
): void {
  if (items.length === 0) return;
  safeWriteUpsert(items);
  for (const item of items) {
    lastPersistedFingerprint.set(
      item.conversationId,
      questionsFingerprint(item.questions)
    );
  }
}

function schedulePersistQuestionsRegistry(
  conversationId: string,
  questions: ConversationQuestion[]
): void {
  const fingerprint = questionsFingerprint(questions);
  if (lastPersistedFingerprint.get(conversationId) === fingerprint) return;

  void apiClient
    .patchConversationQuestionsRegistry(conversationId, questions)
    .then(() => {
      lastPersistedFingerprint.set(conversationId, fingerprint);
    })
    .catch(() => {
      // Silent — retry on next hydrate or optimistic upsert.
    });
}

export function optimisticUpsertConversationQuestions(
  conversationId: string,
  questions: ConversationQuestion[]
): void {
  safeWriteUpsert([
    {
      conversationId,
      questions,
    },
  ]);
  schedulePersistQuestionsRegistry(conversationId, questions);
}

export function optimisticAppendConversationQuestion(
  conversationId: string,
  question: ConversationQuestion
): void {
  const existing = conversationQuestionsCollection.get(conversationId);
  const questions = [...(existing?.questions ?? []), question];
  optimisticUpsertConversationQuestions(conversationId, questions);
}

/** Sidebar questions from normalized transcript (excludes gatekeeper precision sends). */
export function syncConversationQuestionsFromContent(
  conversationId: string
): void {
  const row = conversationContentCollection.get(conversationId);
  if (!row?.messages?.length) return;
  const derived = deriveConversationQuestionsFromMessages(row.messages);
  // Post-stream Dust snapshots can briefly omit user turns. Never wipe an
  // existing sidebar registry with [] (ConversationCard Skeleton).
  if (derived.length === 0) {
    const existing = conversationQuestionsCollection.get(conversationId);
    if (existing?.questions?.length) return;
  }
  optimisticUpsertConversationQuestions(conversationId, derived);
}

/** After hydrate: reconcile registry with transcript-derived questions. */
export function reconcileQuestionsRegistryAfterHydrate(
  conversationId: string
): void {
  syncConversationQuestionsFromContent(conversationId);
}
